import { Button, Tag } from 'antd';
import { useState } from 'react';
import { EligibilityOverrideModal } from '../components/EligibilityOverrideModal';
import type { EligibilityCheckView } from '../dispatch.api';
import { useEligibilityOverride } from '../hooks/use-eligibility-override';
import { tagColourOf } from '../policies/eligibility-check-tag.policy';
import { canOfferOverride } from '../policies/eligibility-override-visibility.policy';

export interface OverrideWindow {
  windowStart: string;
  windowEnd: string;
  pickup?: { zoneCode: string; at: string };
}

interface EligibilityCheckRowProps {
  check: EligibilityCheckView;
  tripId: string;
  resource: 'truck' | 'trailer' | 'driver';
  resourceId: string;
  /** "Truck KDX 123A", for the modal and the toast. */
  resourceLabel: string;
  /** What the check was judged against; the override is judged again against it. */
  window: OverrideWindow;
  /** The signed-in user holds `override.approve`; the server decides regardless. */
  canApprove: boolean;
  /** Called after an override is recorded, so the host re-reads the eligibility answer. */
  onOverridden: () => void;
}

/**
 * One check of an eligibility answer for a trip's assignment: its reason, an Override action when (and only when) it is an overridable
 * block, and, once overridden, who approved it and why. For the assignment screen to place (gitaumoses4/chukua#51); no route renders it yet.
 */
export function EligibilityCheckRow({ check, tripId, resource, resourceId, resourceLabel, window, canApprove, onOverridden }: EligibilityCheckRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { grant, submitting, error, resetError } = useEligibilityOverride(tripId, onOverridden);
  const subject = `${resourceLabel}: ${check.reason ?? check.code}`;

  return (
    <div className="flex flex-wrap items-center gap-2 py-1">
      <Tag color={tagColourOf(check)}>{check.override ? 'Overridden' : check.status}</Tag>
      <span>{check.reason ?? 'Passed'}</span>
      {check.nonOverridable && check.status === 'block' && <span className="text-sm text-muted">Legal requirement, cannot be overridden</span>}
      {check.override && (
        <span className="text-sm text-muted">
          Overridden{check.override.approver ? ` by ${check.override.approver.name}` : ''}: {check.override.reason}
        </span>
      )}
      {canOfferOverride(check, canApprove) && (
        <>
          <Button size="small" onClick={() => setIsOpen(true)}>
            Override
          </Button>
          <EligibilityOverrideModal
            open={isOpen}
            subject={subject}
            submitting={submitting}
            error={error}
            onCancel={() => {
              resetError();
              setIsOpen(false);
            }}
            onSubmit={async ({ requestedById, reason }) => {
              const isSaved = await grant({ resource, resourceId, checkCode: check.code, reason, requestedById, ...window }, resourceLabel);
              if (isSaved) setIsOpen(false);
              return isSaved;
            }}
          />
        </>
      )}
    </div>
  );
}
